import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import {
  clearSessionToken, getSessionToken, setAuthDiag,
  cacheAuthSnapshot, getCachedAuthSnapshot, hydrateAuthSnapshot,
} from "../lib/sessionStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);

  // Keep the offline snapshot fresh whenever user/profile change (login,
  // profile refresh, or optimistic offline patches) so a cold offline
  // relaunch restores the latest coins/energy/streak.
  useEffect(() => {
    if (user) cacheAuthSnapshot(user, profile);
  }, [user, profile]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
      setOfflineMode(false);
      cacheAuthSnapshot(data.user, data.profile);
      setAuthDiag("me=OK");
      try { localStorage.removeItem("fitjong_ad_debug"); } catch { /* ignore */ }
      return data;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const hadToken = !!getSessionToken();
      // OFFLINE FALLBACK — a NETWORK error (no HTTP status) with a stored
      // token means we simply can't reach the server (airplane mode / no
      // signal), NOT that the session is invalid. Restore the last-known
      // user + profile from cache so the app opens and offline play works.
      // A real 401/403 still logs the user out (handled below).
      if (!status && hadToken) {
        const snap = getCachedAuthSnapshot();
        if (snap?.user) {
          setUser(snap.user);
          setProfile(snap.profile || null);
          setOfflineMode(true);
          setAuthDiag(`me=OFFLINE-CACHE tok=Y`);
          return snap;
        }
      }
      if (status) {
        setAuthDiag(`me=${status}:${detail || "?"} tok=${hadToken ? "Y" : "N"}`);
      } else {
        setAuthDiag(`me=NETERR:${String(e?.message || e).slice(0, 30)} tok=${hadToken ? "Y" : "N"}`);
      }
      setUser(null);
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, skip /me check — AuthCallback will handle.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      // Pull the durable offline snapshot into reach BEFORE refresh so the
      // network-error fallback can find it on a cold offline boot.
      try { await hydrateAuthSnapshot(); } catch { /* ignore */ }
      await refresh();
      setLoading(false);
    })();
    // When connectivity returns, re-validate the session for real.
    const onOnline = () => { refresh(); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [refresh]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    await clearSessionToken();
    // NOTE: we intentionally do NOT clear the biometric credentials here.
    setUser(null);
    setProfile(null);
    setOfflineMode(false);
  };

  return (
    <AuthContext.Provider value={{ user, profile, setProfile, loading, refresh, logout, offlineMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
